/**
 * AI Testing Scheduler - Visual Interface
 * Comprehensive visual interface for managing automated testing schedules
 * Supports cron-based scheduling, manual execution, and analytics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

import {
  Schedule as ScheduleIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  Analytics as AnalyticsIcon,
  Health as HealthIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  Assignment as TemplateIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

// Types
interface Schedule {
  id: string;
  name: string;
  description: string;
  scenarioIds: string[];
  cronExpression: string;
  timezone: string;
  enabled: boolean;
  executionConfig: {
    environment: string;
    platform: string;
    targetUrl: string;
    parallelExecution: boolean;
  };
  demographics: string[];
  priority: string;
  createdBy: number;
  lastExecuted?: string;
  nextExecution?: string;
  executionCount: number;
  successCount: number;
  failureCount: number;
  metadata: {
    category: string;
    tags: string[];
  };
}

interface ScheduleTemplate {
  template_id: string;
  name: string;
  description: string;
  category: string;
  demographic: string;
  cron_expression: string;
  usage_count: number;
  is_system_template: boolean;
}

interface ExecutionHistory {
  execution_id: string;
  scenario_id: string;
  status: string;
  duration: number;
  success_rate: number;
  started_at: string;
  completed_at: string;
}

interface SchedulerAnalytics {
  overview: {
    totalSchedules: number;
    activeSchedules: number;
    queuedExecutions: number;
    runningExecutions: number;
    nextExecution?: string;
  };
  demographics: Record<string, number>;
  categories: Record<string, number>;
  executionStats: {
    last24Hours: {
      total: number;
      successful: number;
      failed: number;
    };
    last7Days: {
      total: number;
      successful: number;
      failed: number;
    };
  };
  performance: {
    averageExecutionTime: number;
    successRate: number;
  };
}

const AITestingScheduler: React.FC = () => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const [analytics, setAnalytics] = useState<SchedulerAnalytics | null>(null);
  const [executionHistory, setExecutionHistory] = useState<Record<string, ExecutionHistory[]>>({});
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [newSchedule, setNewSchedule] = useState<Partial<Schedule>>({
    name: '',
    description: '',
    cronExpression: '0 9 * * *', // 9 AM daily by default
    scenarioIds: [],
    demographics: ['baby_boomer_tech_intermediate'],
    enabled: true,
    priority: 'medium',
    executionConfig: {
      environment: 'staging',
      platform: 'web',
      targetUrl: 'https://app.lifescribe.com',
      parallelExecution: false
    },
    metadata: {
      category: 'general',
      tags: []
    }
  });
  const [filters, setFilters] = useState({
    enabled: undefined as boolean | undefined,
    category: '',
    demographic: ''
  });
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' | 'warning' | 'info' 
  });
  const [loading, setLoading] = useState(false);

  // Demographics and categories configuration
  const demographics = {
    'baby_boomer_tech_novice': { label: 'Tech Novice (55-65)', icon: '🔰', color: '#ff9800' },
    'baby_boomer_tech_intermediate': { label: 'Tech Intermediate (66-75)', icon: '⚖️', color: '#2196f3' },
    'baby_boomer_tech_advanced': { label: 'Tech Advanced (76-80+)', icon: '🚀', color: '#4caf50' },
    'accessibility_focused': { label: 'Accessibility Focused', icon: '♿', color: '#9c27b0' }
  };

  const categories = {
    'daily_regression': { label: 'Daily Regression', icon: '📅' },
    'weekly_audit': { label: 'Weekly Audit', icon: '🔍' },
    'monthly_deep': { label: 'Monthly Deep Testing', icon: '🗓️' },
    'demographic_specific': { label: 'Demographic Specific', icon: '👥' },
    'accessibility_compliance': { label: 'Accessibility Compliance', icon: '♿' },
    'general': { label: 'General Testing', icon: '⚙️' }
  };

  const cronPresets = [
    { label: 'Every Hour', value: '0 * * * *' },
    { label: 'Daily at 9 AM', value: '0 9 * * *' },
    { label: 'Daily at 6 PM', value: '0 18 * * *' },
    { label: 'Weekly (Monday 9 AM)', value: '0 9 * * 1' },
    { label: 'Weekly (Friday 6 PM)', value: '0 18 * * 5' },
    { label: 'Monthly (1st at 9 AM)', value: '0 9 1 * *' },
    { label: 'Weekdays at 9 AM', value: '0 9 * * 1-5' },
    { label: 'Custom', value: 'custom' }
  ];

  // Effects
  useEffect(() => {
    loadSchedules();
    loadTemplates();
    loadAnalytics();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      loadAnalytics(); // Refresh analytics every 30 seconds
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // API functions
  const loadSchedules = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (filters.enabled !== undefined) queryParams.append('enabled', filters.enabled.toString());
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.demographic) queryParams.append('demographic', filters.demographic);

      const response = await fetch(`/api/admin/ai-testing/scheduler/schedules?${queryParams}`);
      if (response.ok) {
        const data = await response.json();
        setSchedules(data.data.schedules || []);
      }
    } catch (error) {
      console.error('Failed to load schedules:', error);
      showSnackbar('Failed to load schedules', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/admin/ai-testing/scheduler/templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.data.templates || []);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch('/api/admin/ai-testing/scheduler/analytics');
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.data);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const loadExecutionHistory = async (scheduleId: string) => {
    try {
      const response = await fetch(`/api/admin/ai-testing/scheduler/schedules/${scheduleId}/history`);
      if (response.ok) {
        const data = await response.json();
        setExecutionHistory(prev => ({
          ...prev,
          [scheduleId]: data.data.executions
        }));
      }
    } catch (error) {
      console.error('Failed to load execution history:', error);
    }
  };

  const createSchedule = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/ai-testing/scheduler/schedules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSchedule)
      });

      if (response.ok) {
        const data = await response.json();
        setSchedules(prev => [...prev, data.data]);
        setShowCreateDialog(false);
        setNewSchedule({
          name: '',
          description: '',
          cronExpression: '0 9 * * *',
          scenarioIds: [],
          demographics: ['baby_boomer_tech_intermediate'],
          enabled: true,
          priority: 'medium',
          executionConfig: {
            environment: 'staging',
            platform: 'web',
            targetUrl: 'https://app.lifescribe.com',
            parallelExecution: false
          },
          metadata: { category: 'general', tags: [] }
        });
        showSnackbar(`Schedule created: ${data.data.name}`, 'success');
      } else {
        throw new Error('Failed to create schedule');
      }
    } catch (error) {
      console.error('Failed to create schedule:', error);
      showSnackbar('Failed to create schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createFromTemplate = async (templateId: string, customizations: any) => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/ai-testing/scheduler/from-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId, customizations })
      });

      if (response.ok) {
        const data = await response.json();
        setSchedules(prev => [...prev, data.data]);
        setShowTemplateDialog(false);
        showSnackbar(`Schedule created from template: ${data.data.name}`, 'success');
      } else {
        throw new Error('Failed to create schedule from template');
      }
    } catch (error) {
      console.error('Failed to create schedule from template:', error);
      showSnackbar('Failed to create schedule from template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const executeSchedule = async (scheduleId: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/ai-testing/scheduler/schedules/${scheduleId}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        showSnackbar(`Schedule execution ${data.data.success ? 'completed' : 'failed'}`, 
                    data.data.success ? 'success' : 'warning');
        loadSchedules(); // Refresh to get updated stats
        loadAnalytics();
      } else {
        throw new Error('Failed to execute schedule');
      }
    } catch (error) {
      console.error('Failed to execute schedule:', error);
      showSnackbar('Failed to execute schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleSchedule = async (scheduleId: string, enabled: boolean) => {
    try {
      const response = await fetch(`/api/admin/ai-testing/scheduler/schedules/${scheduleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });

      if (response.ok) {
        setSchedules(prev => prev.map(s => 
          s.id === scheduleId ? { ...s, enabled } : s
        ));
        showSnackbar(`Schedule ${enabled ? 'enabled' : 'disabled'}`, 'success');
      }
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      showSnackbar('Failed to update schedule', 'error');
    }
  };

  const deleteSchedule = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;

    try {
      const response = await fetch(`/api/admin/ai-testing/scheduler/schedules/${scheduleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSchedules(prev => prev.filter(s => s.id !== scheduleId));
        showSnackbar('Schedule deleted', 'success');
      }
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      showSnackbar('Failed to delete schedule', 'error');
    }
  };

  // Helper functions
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatNextExecution = (nextExecution?: string) => {
    if (!nextExecution) return 'Not scheduled';
    const date = new Date(nextExecution);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Overdue';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours < 1) return `In ${diffMinutes}m`;
    if (diffHours < 24) return `In ${diffHours}h ${diffMinutes}m`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `In ${diffDays}d ${diffHours % 24}h`;
  };

  const getSuccessRate = (schedule: Schedule) => {
    if (schedule.executionCount === 0) return 0;
    return Math.round((schedule.successCount / schedule.executionCount) * 100);
  };

  // Render components
  const renderScheduleCard = (schedule: Schedule) => (
    <Card key={schedule.id} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="h6">
                {categories[schedule.metadata?.category]?.icon} {schedule.name}
              </Typography>
              <Switch
                checked={schedule.enabled}
                onChange={(e) => toggleSchedule(schedule.id, e.target.checked)}
                size="small"
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {schedule.description}
            </Typography>

            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              {schedule.demographics.map(demo => (
                <Chip
                  key={demo}
                  label={demographics[demo]?.label || demo}
                  size="small"
                  icon={<span>{demographics[demo]?.icon}</span>}
                  style={{ backgroundColor: demographics[demo]?.color + '20' }}
                />
              ))}
              <Chip 
                label={`${schedule.scenarioIds.length} scenarios`} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={schedule.cronExpression} 
                size="small" 
                variant="outlined"
                icon={<TimeIcon fontSize="small" />}
              />
              <Chip
                label={`${getSuccessRate(schedule)}% success`}
                size="small"
                color={getSuccessRate(schedule) >= 90 ? 'success' : getSuccessRate(schedule) >= 70 ? 'warning' : 'error'}
              />
            </Box>

            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                <strong>Next:</strong> {formatNextExecution(schedule.nextExecution)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <strong>Executions:</strong> {schedule.executionCount}
              </Typography>
              {schedule.lastExecuted && (
                <Typography variant="caption" color="text.secondary">
                  <strong>Last:</strong> {new Date(schedule.lastExecuted).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" gap={1} ml={2}>
            <Tooltip title="View History">
              <IconButton 
                size="small"
                onClick={() => {
                  setSelectedSchedule(schedule);
                  loadExecutionHistory(schedule.id);
                }}
              >
                <TimelineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Execute Now">
              <IconButton 
                size="small"
                onClick={() => executeSchedule(schedule.id)}
                disabled={loading}
              >
                <PlayIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Schedule">
              <IconButton size="small">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Schedule">
              <IconButton 
                size="small" 
                color="error"
                onClick={() => deleteSchedule(schedule.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderAnalyticsDashboard = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <ScheduleIcon color="primary" />
              <Typography variant="h6">{analytics?.overview.totalSchedules || 0}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Total Schedules</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckIcon color="success" />
              <Typography variant="h6">{analytics?.overview.activeSchedules || 0}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Active Schedules</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <SpeedIcon color="warning" />
              <Typography variant="h6">{analytics?.overview.runningExecutions || 0}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Running Now</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUpIcon color="info" />
              <Typography variant="h6">{analytics?.performance.successRate || 0}%</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Success Rate</Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Execution Stats */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Execution Stats
            </Typography>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Last 24 Hours</Typography>
              <Box display="flex" gap={2}>
                <Chip 
                  label={`${analytics?.executionStats.last24Hours.total || 0} total`}
                  size="small"
                />
                <Chip 
                  label={`${analytics?.executionStats.last24Hours.successful || 0} success`}
                  color="success"
                  size="small"
                />
                <Chip 
                  label={`${analytics?.executionStats.last24Hours.failed || 0} failed`}
                  color="error"
                  size="small"
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Last 7 Days</Typography>
              <Box display="flex" gap={2}>
                <Chip 
                  label={`${analytics?.executionStats.last7Days.total || 0} total`}
                  size="small"
                />
                <Chip 
                  label={`${analytics?.executionStats.last7Days.successful || 0} success`}
                  color="success"
                  size="small"
                />
                <Chip 
                  label={`${analytics?.executionStats.last7Days.failed || 0} failed`}
                  color="error"
                  size="small"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Demographics Distribution */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Demographics Distribution
            </Typography>
            {analytics?.demographics && Object.entries(analytics.demographics).map(([demo, count]) => (
              <Box key={demo} display="flex" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <span>{demographics[demo]?.icon}</span>
                  <Typography variant="body2">{demographics[demo]?.label || demo}</Typography>
                </Box>
                <Chip label={count} size="small" />
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderCreateScheduleDialog = () => (
    <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>Create New Schedule</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Schedule Name"
              value={newSchedule.name}
              onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Cron Expression</InputLabel>
              <Select
                value={cronPresets.find(p => p.value === newSchedule.cronExpression)?.value || 'custom'}
                onChange={(e) => {
                  if (e.target.value !== 'custom') {
                    setNewSchedule({ ...newSchedule, cronExpression: e.target.value });
                  }
                }}
              >
                {cronPresets.map(preset => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={newSchedule.description}
              onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newSchedule.metadata?.category || 'general'}
                onChange={(e) => setNewSchedule({ 
                  ...newSchedule, 
                  metadata: { ...newSchedule.metadata, category: e.target.value }
                })}
              >
                {Object.entries(categories).map(([key, cat]) => (
                  <MenuItem key={key} value={key}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{cat.icon}</span>
                      {cat.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={newSchedule.priority || 'medium'}
                onChange={(e) => setNewSchedule({ ...newSchedule, priority: e.target.value })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
        <Button 
          onClick={createSchedule} 
          variant="contained"
          disabled={!newSchedule.name || !newSchedule.cronExpression || loading}
        >
          Create Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        AI Testing Scheduler
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Automated scheduling and execution of AI testing scenarios
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Schedules" icon={<ScheduleIcon />} />
          <Tab label="Analytics" icon={<AnalyticsIcon />} />
          <Tab label="Templates" icon={<TemplateIcon />} />
        </Tabs>
      </Box>

      {/* Tab 0: Schedules */}
      {activeTab === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" gap={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.enabled === undefined ? 'all' : filters.enabled.toString()}
                  onChange={(e) => {
                    const value = e.target.value === 'all' ? undefined : e.target.value === 'true';
                    setFilters({ ...filters, enabled: value });
                  }}
                >
                  <MenuItem value="all">All Schedules</MenuItem>
                  <MenuItem value="true">Enabled Only</MenuItem>
                  <MenuItem value="false">Disabled Only</MenuItem>
                </Select>
              </FormControl>
              
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadSchedules}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<TemplateIcon />}
                onClick={() => setShowTemplateDialog(true)}
              >
                From Template
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateDialog(true)}
              >
                Create Schedule
              </Button>
            </Box>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {schedules.length === 0 ? (
            <Alert severity="info">
              No schedules found. Create your first automated testing schedule to get started.
            </Alert>
          ) : (
            schedules.map(renderScheduleCard)
          )}
        </Box>
      )}

      {/* Tab 1: Analytics */}
      {activeTab === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Scheduler Analytics</Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadAnalytics}
            >
              Refresh
            </Button>
          </Box>
          
          {analytics ? renderAnalyticsDashboard() : (
            <Alert severity="info">Loading analytics...</Alert>
          )}
        </Box>
      )}

      {/* Tab 2: Templates */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h5" gutterBottom>Schedule Templates</Typography>
          
          {templates.map(template => (
            <Card key={template.template_id} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="h6" gutterBottom>
                      {categories[template.category]?.icon} {template.name}
                      {template.is_system_template && (
                        <Chip label="System" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {template.description}
                    </Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip 
                        label={demographics[template.demographic]?.label || template.demographic}
                        size="small"
                        icon={<span>{demographics[template.demographic]?.icon}</span>}
                      />
                      <Chip label={template.cron_expression} size="small" variant="outlined" />
                      <Chip label={`Used ${template.usage_count} times`} size="small" variant="outlined" />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => createFromTemplate(template.template_id, {})}
                  >
                    Use Template
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Dialogs */}
      {renderCreateScheduleDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AITestingScheduler;